import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gradeLabel, subjectLabel } from '../utils/catalogue';

const timeAgo = (ts) => {
  const ms = ts?.toMillis?.();
  if (!ms) return '';
  const minutes = Math.round((Date.now() - ms) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
};

/** REQ-02 — header bell with unread moderation notifications. */
function NotificationBell({ notifications, unreadCount, markAllRead }) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const openQueue = () => {
    setOpen(false);
    navigate('/moderation');
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center justify-center w-9 h-9 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-400"
        aria-label={unreadCount ? `${unreadCount} unread moderation notifications` : 'Moderation notifications'}
        title="Moderation notifications"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white text-gray-900 rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold">Moderation notifications</p>
            {unreadCount > 0 && (
              <button type="button" onClick={markAllRead} className="text-xs font-medium text-brand-600 hover:text-brand-700">
                Mark all read
              </button>
            )}
          </div>
          <ul className="max-h-80 overflow-y-auto divide-y divide-gray-50">
            {notifications.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-gray-500">No notifications yet.</li>
            )}
            {notifications.slice(0, 20).map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={openQueue}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${n.read ? '' : 'bg-brand-50/50'}`}
                >
                  <p className="text-sm font-medium text-gray-800 break-words">
                    {!n.read && <span className="inline-block w-2 h-2 rounded-full bg-brand-600 mr-1.5 align-middle" />}
                    {n.type === 'bulk_upload' ? n.title : `New upload: ${n.title}`}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {subjectLabel(n.subject)} · {gradeLabel(n.grade)}
                    {n.unassigned && <span className="ml-1 text-amber-700">· no moderator assigned</span>}
                    {' · '}{timeAgo(n.createdAt)}
                  </p>
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={openQueue}
            className="block w-full px-4 py-2.5 text-center text-sm font-medium text-brand-600 hover:bg-gray-50 border-t border-gray-100"
          >
            Open moderation queue
          </button>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
